import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  MenuItem,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import TabelaArquivos from './components/TabelaArquivos';
import { IBackupArquivo, TipoTarefa, ITarefaItem } from './types';
import { CentralBackupsApi } from './services/centralBackupsApi';

interface PesquisaArquivosProps {
  clienteID: string;
  token: string;
  onBaixar: (arquivoID: string, nome: string) => void;
  baixandoArquivoID: string | null;
}

export const PesquisaArquivos: React.FC<PesquisaArquivosProps> = ({
  clienteID,
  token,
  onBaixar,
  baixandoArquivoID
}) => {
  const theme = useTheme();
  const [arquivos, setArquivos] = useState<IBackupArquivo[]>([]);
  const [total, setTotal] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // Dropdown de tarefas
  const [tarefasDisponiveis, setTarefasDisponiveis] = useState<ITarefaItem[]>([]);

  // Filtros Básicos
  const [filtroBusca, setFiltroBusca] = useState('');
  const [buscaAtiva, setBuscaAtiva] = useState('');
  
  // Filtros Avançados (dentro de sanfona / Accordion)
  const [tarefaSelecionadaID, setTarefaSelecionadaID] = useState('Todas');
  const [dataInicial, setDataInicial] = useState('');
  const [dataFinal, setDataFinal] = useState('');
  const [tipoTarefa, setTipoTarefa] = useState<TipoTarefa | 'Todos'>('Todos');
  const [situacaoArquivo, setSituacaoArquivo] = useState<'Todos' | 'disponivel' | 'Excluido' | string>('Todos');
  const [tamanhoMinimoMB, setTamanhoMinimoMB] = useState('');
  const [tamanhoMaximoMB, setTamanhoMaximoMB] = useState('');
  const [tamanhoMinimoAtivo, setTamanhoMinimoAtivo] = useState('');
  const [tamanhoMaximoAtivo, setTamanhoMaximoAtivo] = useState('');

  // Paginação e Ordenação
  const [pagina, setPagina] = useState(0);
  const [linhasPorPagina, setLinhasPorPagina] = useState(10);
  const ordenarPor = 'dataCadastro';
  const direcao = 'DESC';

  // Carregar tarefas para preencher o filtro
  useEffect(() => {
    CentralBackupsApi.obterTarefas(clienteID, token)
      .then((data) => {
        const itens = data.items.map(t => ({ id: t.tarefaAgendadaID, nome: t.nome }));
        setTarefasDisponiveis(itens);
      })
      .catch((err) => {
        console.error("Erro ao carregar dropdown de tarefas na busca", err);
      });
  }, [clienteID, token]);

  const executarPesquisa = useCallback(() => {
    setCarregando(true);
    setErro(null);

    const offset = pagina * linhasPorPagina;

    // Converter datas YYYY-MM-DD para ISO
    const dataInicialISO = dataInicial ? `${dataInicial}T00:00:00.000Z` : undefined;
    const dataFinalISO = dataFinal ? `${dataFinal}T23:59:59.999Z` : undefined;

    // Converter tamanho em MB para Bytes (1 MB = 1.048.576 Bytes)
    const tamanhoMinimoBytes = tamanhoMinimoAtivo ? Number(tamanhoMinimoAtivo) * 1024 * 1024 : undefined;
    const tamanhoMaximoBytes = tamanhoMaximoAtivo ? Number(tamanhoMaximoAtivo) * 1024 * 1024 : undefined;

    CentralBackupsApi.pesquisarArquivos(token, {
      clienteID,
      q: buscaAtiva || undefined,
      tarefaAgendadaID: tarefaSelecionadaID === 'Todas' ? undefined : tarefaSelecionadaID,
      dataInicial: dataInicialISO,
      dataFinal: dataFinalISO,
      tipo: tipoTarefa === 'Todos' ? undefined : tipoTarefa,
      status: situacaoArquivo === 'Todos' ? undefined : situacaoArquivo,
      tamanhoMinimoBytes,
      tamanhoMaximoBytes,
      limit: linhasPorPagina,
      offset,
      ordenarPor,
      direcao
    })
      .then((data) => {
        setArquivos(data.items);
        setTotal(data.total);
        setCarregando(false);
      })
      .catch((err) => {
        console.error("Erro ao executar pesquisa avançada", err);
        setErro("Não foi possível realizar a pesquisa avançada de arquivos.");
        setCarregando(false);
      });
  }, [
    clienteID,
    token,
    pagina,
    linhasPorPagina,
    tarefaSelecionadaID,
    dataInicial,
    dataFinal,
    tipoTarefa,
    situacaoArquivo,
    buscaAtiva,
    tamanhoMinimoAtivo,
    tamanhoMaximoAtivo
  ]);

  useEffect(() => {
    executarPesquisa();
  }, [executarPesquisa]);

  const handleSubmeter = (e: React.FormEvent) => {
    e.preventDefault();
    setPagina(0);
    setBuscaAtiva(filtroBusca);
    setTamanhoMinimoAtivo(tamanhoMinimoMB);
    setTamanhoMaximoAtivo(tamanhoMaximoMB);
  };

  const limparFiltros = () => {
    setFiltroBusca('');
    setBuscaAtiva('');
    setTarefaSelecionadaID('Todas');
    setDataInicial('');
    setDataFinal('');
    setTipoTarefa('Todos');
    setSituacaoArquivo('Todos');
    setTamanhoMinimoMB('');
    setTamanhoMaximoMB('');
    setTamanhoMinimoAtivo('');
    setTamanhoMaximoAtivo('');
    setPagina(0);
  };  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 3 }}>
        Pesquisa Avançada de Arquivos
      </Typography>

      {/* Formulário de Filtros */}
      <Paper
        elevation={0}
        component="form"
        onSubmit={handleSubmeter}
        sx={{
          backgroundColor: theme.palette.mode === 'dark' ? '#0F172A' : 'rgba(0, 0, 0, 0.02)',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '12px',
          p: 2.5,
          mb: 4
        }}
      >
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={8} md={9}>
            <TextField
              placeholder="Buscar arquivo por nome ou padrão..."
              value={filtroBusca}
              onChange={(e) => setFiltroBusca(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                sx: { backgroundColor: theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.4)' : 'rgba(255, 255, 255, 0.8)' }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3} sx={{ display: 'flex', gap: 1 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ textTransform: 'none', height: '40px' }}
            >
              Pesquisar
            </Button>
            <IconButton
              onClick={limparFiltros}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px',
                color: 'text.secondary',
                width: '40px',
                height: '40px',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'rgba(42, 81, 247, 0.04)',
                  color: 'primary.main'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>

        {/* Acordeão de Filtros Adicionais */}
        <Accordion
          elevation={0}
          sx={{
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.2)' : 'rgba(0, 0, 0, 0.01)',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '8px !important',
            color: 'text.primary',
            '&:before': { display: 'none' }
          }}
        >
          <AccordionSummary
            expandIcon={<TuneIcon sx={{ color: theme.palette.primary.main }} />}
            sx={{ minHeight: '44px', '.MuiAccordionSummary-content': { my: 1 } }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
              Filtros Avançados de Busca
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 1, pb: 2.5 }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  label="Filtrar por Tarefa"
                  value={tarefaSelecionadaID}
                  onChange={(e) => {
                    setTarefaSelecionadaID(e.target.value);
                    setPagina(0);
                  }}
                  fullWidth
                  size="small"
                  InputLabelProps={{ sx: { color: 'text.secondary' } }}
                >
                  <MenuItem value="Todas">Todas as Tarefas</MenuItem>
                  {tarefasDisponiveis.map((tarefa) => (
                    <MenuItem key={tarefa.id} value={tarefa.id}>
                      {tarefa.nome}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  label="Tipo de Tarefa"
                  value={tipoTarefa}
                  onChange={(e) => {
                    setTipoTarefa(e.target.value as any);
                    setPagina(0);
                  }}
                  fullWidth
                  size="small"
                  InputLabelProps={{ sx: { color: 'text.secondary' } }}
                >
                  <MenuItem value="Todos">Todos os tipos</MenuItem>
                  <MenuItem value="SQLServer">SQL Server</MenuItem>
                  <MenuItem value="Arquivos">Arquivos</MenuItem>
                  <MenuItem value="Bat">Script/BAT</MenuItem>
                  <MenuItem value="Sybase">Sybase</MenuItem>
                  <MenuItem value="Postgres">PostgreSQL</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  label="Situação do Arquivo"
                  value={situacaoArquivo}
                  onChange={(e) => {
                    setSituacaoArquivo(e.target.value);
                    setPagina(0);
                  }}
                  fullWidth
                  size="small"
                  InputLabelProps={{ sx: { color: 'text.secondary' } }}
                >
                  <MenuItem value="Todos">Todas as situações</MenuItem>
                  <MenuItem value="disponivel">Disponível (Recebido)</MenuItem>
                  <MenuItem value="Excluido">Excluído</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                {/* Dummy column spacing */}
                <Box />
              </Grid>

              <Grid item xs={6} sm={3}>
                <TextField
                  type="date"
                  label="De (Cadastro)"
                  value={dataInicial}
                  onChange={(e) => {
                    setDataInicial(e.target.value);
                    setPagina(0);
                  }}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={6} sm={3}>
                <TextField
                  type="date"
                  label="Até (Cadastro)"
                  value={dataFinal}
                  onChange={(e) => {
                    setDataFinal(e.target.value);
                    setPagina(0);
                  }}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={6} sm={3}>
                <TextField
                  type="number"
                  label="Tamanho Mínimo (MB)"
                  placeholder="Ex: 50"
                  value={tamanhoMinimoMB}
                  onChange={(e) => {
                    setTamanhoMinimoMB(e.target.value);
                    setPagina(0);
                  }}
                  fullWidth
                  size="small"
                  InputLabelProps={{ sx: { color: 'text.secondary' } }}
                />
              </Grid>

              <Grid item xs={6} sm={3}>
                <TextField
                  type="number"
                  label="Tamanho Máximo (MB)"
                  placeholder="Ex: 1024"
                  value={tamanhoMaximoMB}
                  onChange={(e) => {
                    setTamanhoMaximoMB(e.target.value);
                    setPagina(0);
                  }}
                  fullWidth
                  size="small"
                  InputLabelProps={{ sx: { color: 'text.secondary' } }}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Paper>
      {/* Erro ou Conteúdo */}
      {erro && (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography variant="body1" color="error" sx={{ mb: 2 }}>
            {erro}
          </Typography>
          <Button variant="outlined" onClick={executarPesquisa}>
            Tentar Novamente
          </Button>
        </Box>
      )}

      {carregando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        !erro && (
          <TabelaArquivos
            arquivos={arquivos}
            total={total}
            pagina={pagina}
            linhasPorPagina={linhasPorPagina}
            onMudarPagina={(novaPagina) => setPagina(novaPagina)}
            onMudarLinhasPorPagina={(novasLinhas) => {
              setLinhasPorPagina(novasLinhas);
              setPagina(0);
            }}
            onBaixar={onBaixar}
            baixandoArquivoID={baixandoArquivoID}
            token={token}
            mostrarTarefa={true}
          />
        )
      )}
    </Box>
  );
};

export default PesquisaArquivos;
